import React, { useState } from 'react';
import { addDays, fmtDay, habitDue, habitStreak, relativeDay, uid } from '../myDayUtils';

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const STRIP_DAYS = 21;

/**
 * Daily habits with streaks. Each row is a 21-day state strip — done / missed /
 * not scheduled — so the state is never colour-alone: every cell carries a
 * tooltip and the legend spells the three states out.
 */
export default function HabitsView({ habits, today, onChange }) {
  const [name, setName] = useState('');
  const [days, setDays] = useState([]);

  const strip = Array.from({ length: STRIP_DAYS }, (_, i) => addDays(today, -(STRIP_DAYS - 1 - i)));

  const add = e => {
    e.preventDefault();
    const clean = name.trim();
    if (!clean) return;
    onChange([...habits, { id: uid(), name: clean, days: [...days].sort(), log: {}, createdAt: Date.now() }]);
    setName('');
    setDays([]);
  };

  const toggleDay = (habit, date) => onChange(habits.map(h => {
    if (h.id !== habit.id) return h;
    const log = { ...(h.log || {}) };
    if (log[date]) delete log[date]; else log[date] = true;
    return { ...h, log };
  }));

  const remove = habit => {
    if (!window.confirm(`Delete the habit "${habit.name}" and its history?`)) return;
    onChange(habits.filter(h => h.id !== habit.id));
  };

  return (
    <div className="md-habits">
      <p className="md-lead">
        The small things that only pay off if they happen every day. Tap any square to tick
        a day off — today is the square on the right.
      </p>

      <form className="md-habits__add" onSubmit={add}>
        <input className="md-input" value={name} placeholder="New habit — e.g. Check tomorrow's prep list"
          onChange={e => setName(e.target.value)} aria-label="New habit name" />
        <div className="md-habits__dows" role="group" aria-label="Days this habit is due">
          {DOW.map((d, i) => (
            <button key={i} type="button"
              className={`md-dow${days.includes(i) ? ' md-dow--on' : ''}`}
              onClick={() => setDays(v => (v.includes(i) ? v.filter(x => x !== i) : [...v, i]))}
              aria-pressed={days.includes(i)}
              title={['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][i]}
            >{d}</button>
          ))}
        </div>
        <button className="md-btn md-btn--primary" type="submit" disabled={!name.trim()}>Add habit</button>
      </form>
      <p className="md-hint">Leave the day buttons untouched for an every-day habit.</p>

      <div className="md-legend" aria-hidden="true">
        <span className="md-legend__item"><i className="md-cell md-cell--done" /> Done</span>
        <span className="md-legend__item"><i className="md-cell md-cell--miss" /> Missed</span>
        <span className="md-legend__item"><i className="md-cell md-cell--off" /> Not scheduled</span>
      </div>

      {habits.length === 0 ? (
        <p className="md-empty">No habits yet. Start with one — a streak you can see is the point.</p>
      ) : (
        <ul className="md-habits__list">
          {habits.map(h => {
            const s = habitStreak(h, today);
            const doneToday = !!(h.log || {})[today];
            return (
              <li key={h.id} className="md-habit">
                <div className="md-habit__head">
                  <button
                    className={`md-check${doneToday ? ' md-check--on' : ''}`}
                    onClick={() => toggleDay(h, today)}
                    aria-pressed={doneToday}
                    aria-label={`${doneToday ? 'Untick' : 'Tick'} ${h.name} for today`}
                    title={doneToday ? 'Untick today' : 'Tick today'}
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                  <span className="md-habit__name">{h.name}</span>
                  <span className="md-habit__streak" title="Current streak">🔥 {s.current}</span>
                  <span className="md-habit__best">best {s.best}</span>
                  <button className="md-iconbtn md-iconbtn--danger" onClick={() => remove(h)} aria-label={`Delete ${h.name}`}>✕</button>
                </div>
                <div className="md-habit__strip">
                  {strip.map(date => {
                    const due = habitDue(h, date);
                    const done = !!(h.log || {})[date];
                    const state = !due ? 'off' : done ? 'done' : 'miss';
                    return (
                      <button
                        key={date}
                        className={`md-cell md-cell--${state}`}
                        onClick={() => toggleDay(h, date)}
                        title={`${fmtDay(date)} — ${state === 'done' ? 'done' : state === 'miss' ? 'missed' : 'not scheduled'}`}
                        aria-label={`${relativeDay(date, today)}: ${state}`}
                      />
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
